import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { PortalFooter } from "@/components/PortalFooter";
import { useAdminAuth } from "@/lib/adminAuth";
import { Lock, User, Loader2, ArrowRight, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const nav = useNavigate();
  const { login } = useAdminAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!username.trim() || !password) {
      toast.error("Enter username and password");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: username.trim(), password }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Invalid credentials");
      }
      const session = await res.json();
      login(session);
      toast.success(`Welcome, ${session.name}`);
      nav("/admin", { replace: true });
    } catch (err) {
      toast.error(err.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F7F7F7]">
      <header className="border-b border-[#E5E7EB] bg-white/70 backdrop-blur">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/"><Logo /></Link>
          <div className="text-xs text-[#6B7280] uppercase tracking-widest font-semibold">
            Admin Console
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-14">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="mu-card p-8 md:p-10" data-testid="admin-login-card">
            <div className="h-12 w-12 rounded-2xl bg-[#FBE7EA] flex items-center justify-center mb-6">
              <ShieldCheck className="h-6 w-6 text-[#B60F2A]" />
            </div>
            <div className="tiny-label">Sign in</div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#111] tracking-tight mt-1">
              Admin Console Login
            </h1>
            <p className="text-sm text-[#6B7280] mt-2">
              {/* Student Affairs (saadmin) login disabled — commented, not deleted, in case it's reinstated. */}
              Restricted access for Registrar, Finance, Check-in, Admissions &amp; Super Admin.
            </p>

            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label className="tiny-label" htmlFor="username">Username</label>
                <div className="relative mt-1">
                  <User className="h-4 w-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    id="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter your username"
                    autoComplete="username"
                    className="mu-input pl-9"
                    data-testid="input-admin-username"
                  />
                </div>
              </div>
              <div>
                <label className="tiny-label" htmlFor="password">Password</label>
                <div className="relative mt-1">
                  <Lock className="h-4 w-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    autoComplete="current-password"
                    className="mu-input pl-9 pr-10"
                    data-testid="input-admin-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#111]"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    data-testid="btn-toggle-password"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="mu-btn-primary w-full mt-2"
                disabled={busy}
                data-testid="btn-admin-login"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                {busy ? "Signing in…" : "Sign in"}
              </button>
            </form>
          </div>

          <div className="text-[11px] text-[#9CA3AF] text-center mt-4">
            Mahindra University · Admissions Cell
          </div>
        </motion.div>
      </main>

      <PortalFooter />
    </div>
  );
}
